import React from "react";
import useFormattingUtils from "../../../context/useFormattingUtils";

const POSAppThermalInvoicePreview = ({ open, onClose, invoice, storeInfo }) => {
  const { formatDate, formatCurrency } = useFormattingUtils();

  if (!open) return null;

  const safeNumber = (value) => {
    const number = parseFloat(value);
    return Number.isNaN(number) ? 0 : number;
  };

  const safeValue = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") return fallback;
    return value;
  };

  const safeCurrency = (value) => {
    const amount = safeNumber(value);
    return formatCurrency ? formatCurrency(amount) : amount.toFixed(2);
  };

  const getLineItems = () => {
    const candidates = [
      invoice?.items,
      invoice?.products,
      invoice?.invoice_products,
      invoice?.pos_invoice_products,
      invoice?.sales_invoice_products,
      invoice?.product_details,
      invoice?.details,
    ];

    const found = candidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0);
    return Array.isArray(found) ? found : [];
  };

  const lineItems = getLineItems();

  const payableAmount =
    safeNumber(invoice?.payable_amount) ||
    safeNumber(invoice?.total_amount) ||
    safeNumber(invoice?.final_after_discount);

  const totalQty =
    safeNumber(invoice?.total_qty) ||
    lineItems.reduce((total, item) => total + safeNumber(item?.qty || item?.quantity || item?.sold_qty_stock), 0);

  const subtotal =
    lineItems.reduce(
      (total, item) =>
        total +
        safeNumber(
          item?.total_amount ||
            item?.amount ||
            item?.after_discount ||
            item?.total ||
            0
        ),
      0
    ) || payableAmount;

  const discount =
    safeNumber(invoice?.discount) ||
    safeNumber(invoice?.discount_amount) ||
    safeNumber(invoice?.additional_discount_value);

  const roundOff = safeNumber(invoice?.round_off);

  const handlePrint = () => {
    const printArea = document.getElementById("pos-app-thermal-print-area");

    if (!printArea) {
      window.print();
      return;
    }

    setTimeout(() => {
      window.focus();
      window.print();
    }, 150);
  };

  const lineStyle = {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    fontSize: "12px",
    lineHeight: 1.45,
  };

  const mutedText = {
    color: "#64748b",
    fontSize: "11px",
    lineHeight: 1.45,
  };

  return (
    <>
      <style>
        {`
          @media print {
            html,
            body {
              background: #ffffff !important;
              margin: 0 !important;
              padding: 0 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            body * {
              visibility: hidden !important;
            }

            #pos-app-thermal-print-area,
            #pos-app-thermal-print-area * {
              visibility: visible !important;
            }

            #pos-app-thermal-print-area {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 80mm !important;
              max-width: 80mm !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #ffffff !important;
              box-shadow: none !important;
              border: none !important;
              border-radius: 0 !important;
            }

            .posapp-thermal-no-print,
            .posapp-thermal-no-print * {
              display: none !important;
              visibility: hidden !important;
            }

            @page {
              size: 80mm auto;
              margin: 4mm;
            }
          }

          @media (max-width: 760px) {
            .posapp-thermal-card {
              width: 96% !important;
              max-height: 92vh !important;
            }

            .posapp-thermal-layout {
              grid-template-columns: 1fr !important;
            }

            .posapp-thermal-actions {
              width: 100%;
              justify-content: stretch !important;
            }

            .posapp-thermal-actions button {
              flex: 1;
            }
          }
        `}
      </style>

      <div
        style={{
          position: "fixed",
          inset: 0,
          background:
            "radial-gradient(circle at top, rgba(15, 23, 42, 0.72), rgba(2, 6, 23, 0.86))",
          zIndex: 1200,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "24px",
        }}
      >
        <div
          className="posapp-thermal-card"
          style={{
            width: "790px",
            maxHeight: "90vh",
            overflowY: "auto",
            background: "#f8fafc",
            borderRadius: "24px",
            boxShadow: "0 28px 80px rgba(2, 6, 23, 0.42)",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <div
            className="posapp-thermal-no-print"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              background: "#ffffff",
              borderBottom: "1px solid #e5e7eb",
              padding: "16px 18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "14px",
              borderTopLeftRadius: "24px",
              borderTopRightRadius: "24px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontSize: "18px", fontWeight: 950, color: "#0f172a" }}>
                Thermal Receipt Preview
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 700, marginTop: "2px" }}>
                POS app receipt optimized for 80mm thermal printer.
              </div>
            </div>

            <div
              className="posapp-thermal-actions"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <button
                type="button"
                onClick={handlePrint}
                style={{
                  background: "#0f172a",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "999px",
                  padding: "10px 18px",
                  fontSize: "13px",
                  fontWeight: 950,
                  cursor: "pointer",
                }}
              >
                Print Thermal Receipt
              </button>

              <button
                type="button"
                onClick={onClose}
                style={{
                  background: "#ffffff",
                  color: "#111827",
                  border: "1px solid #d1d5db",
                  borderRadius: "999px",
                  width: "38px",
                  height: "38px",
                  fontSize: "19px",
                  fontWeight: 950,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
          </div>

          <div
            className="posapp-thermal-layout"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 320px",
              gap: "24px",
              padding: "24px",
              alignItems: "start",
            }}
          >
            <div className="posapp-thermal-no-print">
              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "22px",
                  padding: "20px",
                  marginBottom: "14px",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    background: "#ecfdf5",
                    color: "#047857",
                    border: "1px solid #a7f3d0",
                    borderRadius: "999px",
                    padding: "7px 12px",
                    fontSize: "12px",
                    fontWeight: 950,
                    marginBottom: "14px",
                  }}
                >
                  POS App Money Collected
                </div>

                <h3
                  style={{
                    margin: 0,
                    fontSize: "30px",
                    fontWeight: 950,
                    color: "#0f172a",
                    letterSpacing: "-0.04em",
                  }}
                >
                  {safeCurrency(payableAmount)}
                </h3>

                <p
                  style={{
                    margin: "8px 0 0",
                    color: "#64748b",
                    fontSize: "13px",
                    lineHeight: 1.6,
                    fontWeight: 700,
                  }}
                >
                  Preview and print the exact compact receipt format for quick POS app transactions.
                </p>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "22px",
                  padding: "18px",
                }}
              >
                <div style={{ fontSize: "15px", fontWeight: 950, color: "#0f172a", marginBottom: "12px" }}>
                  Transaction Summary
                </div>

                <div style={{ display: "grid", gap: "10px" }}>
                  <div style={lineStyle}>
                    <strong>Invoice No</strong>
                    <span>{safeValue(invoice?.invoice_no)}</span>
                  </div>
                  <div style={lineStyle}>
                    <strong>Date</strong>
                    <span>{invoice?.date ? formatDate(invoice.date) : "N/A"}</span>
                  </div>
                  <div style={lineStyle}>
                    <strong>Total Qty</strong>
                    <span>{totalQty}</span>
                  </div>
                  <div style={lineStyle}>
                    <strong>Items</strong>
                    <span>{lineItems.length || "Summary only"}</span>
                  </div>
                </div>
              </div>
            </div>

            <div
              id="pos-app-thermal-print-area"
              style={{
                width: "320px",
                maxWidth: "100%",
                margin: "0 auto",
                background: "#ffffff",
                color: "#111827",
                borderRadius: "10px",
                boxShadow: "0 20px 55px rgba(15, 23, 42, 0.18)",
                border: "1px solid #e5e7eb",
                overflow: "hidden",
                fontFamily:
                  "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
              }}
            >
              <div
                style={{
                  padding: "16px 14px 10px",
                  textAlign: "center",
                  borderBottom: "1px dashed #94a3b8",
                }}
              >
                <div style={{ fontSize: "16px", fontWeight: 950, textTransform: "uppercase" }}>
                  {safeValue(storeInfo?.store_name, "Store Name")}
                </div>
                <div style={{ ...mutedText, marginTop: "4px" }}>
                  {safeValue(storeInfo?.store_address, "Store Address")}
                </div>
                <div style={mutedText}>
                  Phone: {safeValue(storeInfo?.store_phone, "Phone Number")}
                </div>
                <div
                  style={{
                    marginTop: "10px",
                    fontSize: "13px",
                    fontWeight: 950,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  POS APP RECEIPT
                </div>
              </div>

              <div style={{ padding: "12px 14px", borderBottom: "1px dashed #94a3b8" }}>
                <div style={lineStyle}>
                  <span>Invoice</span>
                  <strong>{safeValue(invoice?.invoice_no)}</strong>
                </div>
                <div style={lineStyle}>
                  <span>Date</span>
                  <strong>{invoice?.date ? formatDate(invoice.date) : "N/A"}</strong>
                </div>
                <div style={lineStyle}>
                  <span>Payment</span>
                  <strong style={{ textTransform: "capitalize" }}>
                    {safeValue(invoice?.pay_method || invoice?.payment_method, "N/A")}
                  </strong>
                </div>
              </div>

              <div style={{ padding: "10px 14px", borderBottom: "1px dashed #94a3b8" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 42px 70px",
                    gap: "8px",
                    fontSize: "11px",
                    fontWeight: 950,
                    color: "#334155",
                    paddingBottom: "6px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <span>ITEM</span>
                  <span style={{ textAlign: "center" }}>QTY</span>
                  <span style={{ textAlign: "right" }}>AMT</span>
                </div>

                {lineItems.length > 0 ? (
                  lineItems.map((item, index) => {
                    const productName =
                      item?.product_name || item?.title || item?.name || item?.product || "Product";
                    const qty = item?.qty || item?.quantity || item?.sold_qty_stock || 0;
                    const rate = item?.rate || item?.selling_price || item?.price || 0;
                    const amount =
                      item?.after_discount ||
                      item?.total ||
                      item?.total_amount ||
                      item?.amount ||
                      0;

                    return (
                      <div
                        key={item?.id || index}
                        style={{
                          padding: "8px 0",
                          borderBottom:
                            index === lineItems.length - 1 ? "none" : "1px solid #f1f5f9",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 42px 70px",
                            gap: "8px",
                            alignItems: "start",
                          }}
                        >
                          <div>
                            <div style={{ fontSize: "12px", fontWeight: 900, lineHeight: 1.35 }}>
                              {index + 1}. {productName}
                            </div>
                            <div style={{ ...mutedText, marginTop: "2px" }}>
                              Rate {safeCurrency(rate)}
                            </div>
                          </div>
                          <div style={{ fontSize: "12px", textAlign: "center", fontWeight: 900 }}>
                            {qty}
                          </div>
                          <div style={{ fontSize: "12px", textAlign: "right", fontWeight: 950 }}>
                            {safeCurrency(amount)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: "10px 0" }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 42px 70px",
                        gap: "8px",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "12px", fontWeight: 900 }}>
                          1. POS Transaction Total
                        </div>
                        <div style={{ ...mutedText, marginTop: "2px" }}>
                          Summary receipt
                        </div>
                      </div>
                      <div style={{ fontSize: "12px", textAlign: "center", fontWeight: 900 }}>
                        {totalQty}
                      </div>
                      <div style={{ fontSize: "12px", textAlign: "right", fontWeight: 950 }}>
                        {safeCurrency(payableAmount)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ padding: "12px 14px", borderBottom: "1px dashed #94a3b8" }}>
                <div style={lineStyle}>
                  <span>Total Qty</span>
                  <strong>{totalQty}</strong>
                </div>
                <div style={lineStyle}>
                  <span>Subtotal</span>
                  <strong>{safeCurrency(subtotal)}</strong>
                </div>
                <div style={lineStyle}>
                  <span>Discount</span>
                  <strong>{safeCurrency(discount)}</strong>
                </div>
                <div style={lineStyle}>
                  <span>Round Off</span>
                  <strong>{safeCurrency(roundOff)}</strong>
                </div>
              </div>

              <div
                style={{
                  padding: "13px 14px",
                  background: "#f8fafc",
                  borderBottom: "1px dashed #94a3b8",
                }}
              >
                <div style={{ ...lineStyle, alignItems: "center" }}>
                  <span style={{ fontSize: "14px", fontWeight: 950 }}>PAID</span>
                  <strong style={{ fontSize: "20px" }}>{safeCurrency(payableAmount)}</strong>
                </div>
              </div>

              <div
                style={{
                  padding: "14px",
                  textAlign: "center",
                  fontSize: "11px",
                  color: "#475569",
                  lineHeight: 1.55,
                  fontWeight: 700,
                }}
              >
                <div style={{ color: "#111827", fontWeight: 950, marginBottom: "4px" }}>
                  Thank you for your purchase!
                </div>
                <div>Keep this receipt for your records.</div>
                <div style={{ marginTop: "8px" }}>Powered by Paisavara POS App</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default POSAppThermalInvoicePreview;
